import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { partnerFetch, partnerSave } from '../../../actions';
import { useTranslation } from 'react-i18next';
import LoadingOverlay from '../../common/LoadingOverlay';

import useForm from '../../../hooks/useForm';
import usePrevious from '../../../hooks/usePrevious';

function OrganizationSettings() {
    const { data, loading, saving, error } = useSelector(({ partner }) => partner);
    const partnerID = localStorage.getItem('partnerID');
    const partner = data;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const prevLoading = usePrevious(loading);
    const [initialLoading, setInitialLoading] = useState(true);

    const { values, handleInputChange, handleSubmit } = useForm(() => {
        dispatch(partnerSave(partnerID, { partnerID, ...values }, t));
    });

    useEffect(() => {
        dispatch(partnerFetch(partnerID));
    }, [dispatch, partnerID]);

    useEffect(() => {
        if (loading !== prevLoading) {
            if (!loading && initialLoading) {
                setInitialLoading(false);
            }
        }
    }, [loading, prevLoading, initialLoading])

    return (
        <Row>
            {!initialLoading ?
                <Col lg={8}>
                    <Form onSubmit={handleSubmit}>
                        <Card>
                            <Card.Header>
                                {t('settings.partner_header')}
                            </Card.Header>
                            <Card.Body style={{ position: 'relative' }}>
                                {loading ?
                                    <LoadingOverlay />
                                : null}
                                {error &&
                                    <Alert variant="danger"><strong>{error.title}</strong>: {error.details}</Alert>
                                }
                                <fieldset>
                                    <legend>{t('settings.basic_details')}</legend>
                                    <Form.Group as={Row} controlId="partnerName">
                                        <Form.Label column sm={3}>{t('general.partner_name')}</Form.Label>
                                        <Col sm={9}>
                                            <Form.Control type="text" name="name" defaultValue={partner.name} onChange={handleInputChange} required />
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="contactEmail">
                                        <Form.Label column sm={3}>{t('general.email')}</Form.Label>
                                        <Col sm={9}>
                                            <Form.Control type="email" name="contactEmail" defaultValue={partner.contactEmail} onChange={handleInputChange} required />
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="businessID">
                                        <Form.Label column sm={3}>{t('general.business_id')}</Form.Label>
                                        <Col sm={9}>
                                            <Form.Control type="text" name="businessID" defaultValue={partner.businessID} onChange={handleInputChange} />
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="streetAddress">
                                        <Form.Label column sm={3}>{t('general.street_address')}</Form.Label>
                                        <Col sm={9}>
                                            <Form.Control type="text" name="streetAddress" defaultValue={partner.streetAddress} onChange={handleInputChange} />
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="postalCode">
                                        <Form.Label column sm={3}>{t('general.postal_code')}</Form.Label>
                                        <Col sm={9}>
                                            <Form.Control type="text" name="postalCode" defaultValue={partner.postalCode} onChange={handleInputChange} />
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="city">
                                        <Form.Label column sm={3}>{t('general.city')}</Form.Label>
                                        <Col sm={9}>
                                            <Form.Control type="text" name="city" defaultValue={partner.city} onChange={handleInputChange} />
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="homepage">
                                        <Form.Label column sm={3}>{t('general.homepage')}</Form.Label>
                                        <Col sm={9}>
                                            <Form.Control type="text" name="homepage" defaultValue={partner.homepage} onChange={handleInputChange} />
                                        </Col>
                                    </Form.Group>
                                </fieldset>
                                <fieldset>
                                    <legend>{t('general.billing_details')}</legend>
                                    <Form.Group as={Row} controlId="invoiceCompany">
                                        <Form.Label column sm={3}>{t('general.company')}</Form.Label>
                                        <Col sm={7}>
                                            <Form.Control type="text" name="invoiceCompany" defaultValue={partner.invoiceCompany} onChange={handleInputChange} />
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="invoiceStreetAddress">
                                        <Form.Label column sm={3}>{t('general.street_address')}</Form.Label>
                                        <Col sm={8}>
                                            <Form.Control type="text" name="invoiceStreetAddress" defaultValue={partner.invoiceStreetAddress} onChange={handleInputChange} />
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="invoicePostalCode">
                                        <Form.Label column sm={3}>{t('general.postal_code')}</Form.Label>
                                        <Col sm={2}>
                                            <Form.Control type="text" name="invoicePostalCode" defaultValue={partner.invoicePostalCode} onChange={handleInputChange} />
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="invoiceCity">
                                        <Form.Label column sm={3}>{t('general.city')}</Form.Label>
                                        <Col sm={5}>
                                            <Form.Control type="text" name="invoiceCity" defaultValue={partner.invoiceCity} onChange={handleInputChange} />
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="invoiceCompanyBusinessID">
                                        <Form.Label column sm={3}>{t('general.business_id')}</Form.Label>
                                        <Col sm={9}>
                                            <Form.Control type="text" name="invoiceCompanyBusinessID" defaultValue={partner.invoiceCompanyBusinessID} onChange={handleInputChange} />
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="vatNumber">
                                        <Form.Label column sm={3}>{t('general.vat')}</Form.Label>
                                        <Col sm={5}>
                                            <Form.Control type="text" name="vatNumber" defaultValue={partner.vatNumber} onChange={handleInputChange} />
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="eInvoiceAddress">
                                        <Form.Label column sm={3}>{t('general.einvoice_address')}</Form.Label>
                                        <Col sm={5}>
                                            <Form.Control type="text" name="eInvoiceAddress" defaultValue={partner.eInvoiceAddress} onChange={handleInputChange} />
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="eInvoiceOperator">
                                        <Form.Label column sm={3}>{t('general.einvoice_operator')}</Form.Label>
                                        <Col sm={5}>
                                            <Form.Control type="text" name="eInvoiceOperator" defaultValue={partner.eInvoiceOperator} onChange={handleInputChange} />
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} controlId="eInvoiceIntermediateId">
                                        <Form.Label column sm={3}>{t('general.einvoice_intermediate_id')}</Form.Label>
                                        <Col sm={5}>
                                            <Form.Control type="text" name="eInvoiceIntermediateId" defaultValue={partner.eInvoiceIntermediateId} onChange={handleInputChange} />
                                        </Col>
                                    </Form.Group>
                                </fieldset>
                            </Card.Body>

                            <Card.Footer>
                                <Form.Group as={Row}>
                                    <Col sm={{ span: 9, offset: 3 }}>
                                        <Button type="submit" variant="success" disabled={(loading || saving)}>{t('general.save_changes')}</Button>
                                    </Col>
                                </Form.Group>
                            </Card.Footer>
                        </Card>
                    </Form>
                </Col>
            :
                <Col lg={8}>
                    <div style={{ position: 'relative' }}>
                        <LoadingOverlay variant="dark" />
                    </div>
                </Col>
            }
        </Row>
    );
}

export default OrganizationSettings;